<script>
  import Layout from '../components/Layout.svelte';
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';

  let loading = true;
  let savingExpense = false;
  let savingSales = false;
  let refreshing = false;
  let error = null;
  let success = null;

  // Data
  let jtCostCodes = [];
  let xeroAccounts = [];

  // Two parallel maps. Same shape: { costCodeNumber: xeroAccountCode }.
  // costCodeMappings → Xero EXPENSE/PURCHASE accounts, used by vendor bill sync.
  // salesCodeMappings → Xero SALES/COGS accounts, used by Xero invoice sync.
  let costCodeMappings = {};
  let salesCodeMappings = {};

  let selectedJtCodes = new Set();
  let selectedExpenseCode = '';
  let selectedSalesCode = '';
  let expandedParents = new Set(); // Track which parent codes are expanded
  let salesMappingBannerDismissed = false;
  // Non-owner members inherit the owner's mappings and cannot edit them.
  let managedByOwner = false;

  // Migration banner state: shown when user has expense mappings but
  // hasn't configured the matching sales mappings yet.
  $: missingSalesMappings = (() => {
    const expenseKeys = Object.keys(costCodeMappings);
    if (expenseKeys.length === 0) return 0;
    return expenseKeys.filter(k => !salesCodeMappings[k]).length;
  })();

  // Load all data on mount
  onMount(async () => {
    // QuickBooks users manage cost-code → account mappings inside JobTread's
    // native QuickBooks setup, not in Buildzilla. This page is Xero-only, so
    // redirect them back to Settings rather than loading (Xero) account data.
    try {
      const res = await fetch('/api/users/integrations');
      if (res.ok) {
        const data = await res.json();
        if (data.accountingPlatform === 'quickbooks') {
          navigate('/settings');
          return;
        }
        managedByOwner = !!data.managedByOwner;
      }
    } catch (_) { /* fall through to normal load */ }

    await Promise.all([
      loadJtCostCodes(),
      loadXeroAccounts(),
      loadExpenseMappings(),
      loadSalesMappings(),
      loadBannerDismissedState()
    ]);
    loading = false;
  });

  async function loadBannerDismissedState() {
    try {
      const response = await fetch('/api/user');
      const data = await response.json();
      if (response.ok && data.success && data.user) {
        salesMappingBannerDismissed = !!data.user.salesMappingBannerDismissed;
      }
    } catch (err) {
      console.error('Error loading sales mapping banner state:', err);
    }
  }

  async function dismissSalesMappingBanner() {
    salesMappingBannerDismissed = true;
    try {
      await fetch('/api/users/sales-mapping-banner-dismissed', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dismissed: true })
      });
    } catch (err) {
      console.error('Failed to dismiss sales mapping banner:', err);
    }
  }

  async function loadJtCostCodes() {
    try {
      const response = await fetch('/api/users/jobtread-parent-cost-codes');
      const data = await response.json();

      if (response.ok && data.success) {
        jtCostCodes = data.costCodes;
        console.log('Loaded JT cost codes:', jtCostCodes.length);
      } else if (data.needsSetup) {
        error = 'Please configure your JobTread connection in Settings first.';
      } else {
        throw new Error(data.error || 'Failed to load cost codes');
      }
    } catch (err) {
      console.error('Error loading JT cost codes:', err);
      error = 'Failed to load JobTread cost codes. ' + err.message;
    }
  }

  async function loadXeroAccounts() {
    try {
      const response = await fetch('/api/users/xero-chart-accounts');
      const data = await response.json();

      if (response.ok && data.success) {
        // If cache is empty, fetch from Xero
        if (data.accounts.length === 0) {
          await refreshXeroAccounts();
        } else {
          xeroAccounts = data.accounts;
          console.log('Loaded cached Xero accounts:', xeroAccounts.length);
        }
      } else {
        throw new Error(data.error || 'Failed to load Xero accounts');
      }
    } catch (err) {
      console.error('Error loading Xero accounts:', err);
      error = 'Failed to load Xero chart of accounts. ' + err.message;
    }
  }

  async function refreshXeroAccounts(showFeedback = false) {
    try {
      if (showFeedback) {
        refreshing = true;
        error = null;
        success = null;
      }
      console.log('Fetching Xero accounts from API...');
      const response = await fetch('/api/xero/chart-accounts');
      const data = await response.json();

      if (response.ok && data.success && data.accounts) {
        xeroAccounts = data.accounts;

        // Cache the accounts
        await fetch('/api/users/xero-chart-accounts', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accounts: xeroAccounts })
        });

        console.log('Fetched and cached Xero accounts:', xeroAccounts.length);
        if (showFeedback) {
          success = `Refreshed ${xeroAccounts.length} Xero accounts from your organisation.`;
          setTimeout(() => { success = null; }, 5000);
        }
      } else {
        throw new Error(data.error || 'Failed to fetch Xero accounts');
      }
    } catch (err) {
      console.error('Error refreshing Xero accounts:', err);
      if (showFeedback) {
        error = 'Failed to refresh Xero accounts. ' + err.message;
      }
      throw err;
    } finally {
      refreshing = false;
    }
  }

  async function loadExpenseMappings() {
    try {
      const response = await fetch('/api/users/cost-code-mappings');
      const data = await response.json();
      if (response.ok && data.success) {
        costCodeMappings = data.mappings;
        console.log('Loaded expense mappings:', Object.keys(costCodeMappings).length);
      } else {
        throw new Error(data.error || 'Failed to load expense mappings');
      }
    } catch (err) {
      console.error('Error loading expense mappings:', err);
      error = 'Failed to load expense mappings. ' + err.message;
    }
  }

  async function loadSalesMappings() {
    try {
      const response = await fetch('/api/users/sales-code-mappings');
      const data = await response.json();
      if (response.ok && data.success) {
        salesCodeMappings = data.mappings;
        console.log('Loaded sales mappings:', Object.keys(salesCodeMappings).length);
      } else {
        throw new Error(data.error || 'Failed to load sales mappings');
      }
    } catch (err) {
      console.error('Error loading sales mappings:', err);
      error = 'Failed to load sales mappings. ' + err.message;
    }
  }

  function toggleSelection(codeNumber) {
    if (selectedJtCodes.has(codeNumber)) {
      selectedJtCodes.delete(codeNumber);
    } else {
      selectedJtCodes.add(codeNumber);
    }
    selectedJtCodes = selectedJtCodes; // Trigger reactivity
  }

  function toggleExpand(parentNumber) {
    if (expandedParents.has(parentNumber)) {
      expandedParents.delete(parentNumber);
    } else {
      expandedParents.add(parentNumber);
    }
    expandedParents = expandedParents; // Trigger reactivity
  }

  // Assign the selected Xero EXPENSE code to all currently-selected JT codes.
  function handleExpenseAssignment() {
    if (!selectedExpenseCode) return;
    if (selectedJtCodes.size === 0) {
      error = 'Please select at least one JobTread cost code first.';
      return;
    }
    const count = selectedJtCodes.size;
    selectedJtCodes.forEach(jtNumber => {
      costCodeMappings[jtNumber] = selectedExpenseCode;
    });
    costCodeMappings = costCodeMappings;
    selectedJtCodes.clear();
    selectedJtCodes = selectedJtCodes;
    selectedExpenseCode = '';
    error = null;
    success = `Assigned expense account to ${count} cost code(s). Click Save Expense Mappings to persist.`;
  }

  // Assign the selected Xero SALES code to all currently-selected JT codes.
  function handleSalesAssignment() {
    if (!selectedSalesCode) return;
    if (selectedJtCodes.size === 0) {
      error = 'Please select at least one JobTread cost code first.';
      return;
    }
    const count = selectedJtCodes.size;
    selectedJtCodes.forEach(jtNumber => {
      salesCodeMappings[jtNumber] = selectedSalesCode;
    });
    salesCodeMappings = salesCodeMappings;
    selectedJtCodes.clear();
    selectedJtCodes = selectedJtCodes;
    selectedSalesCode = '';
    error = null;
    success = `Assigned sales account to ${count} cost code(s). Click Save Sales Mappings to persist.`;
  }

  function handleClearExpense() {
    if (selectedJtCodes.size === 0) {
      error = 'Please select at least one JobTread cost code to clear.';
      return;
    }
    selectedJtCodes.forEach(jtNumber => { delete costCodeMappings[jtNumber]; });
    costCodeMappings = costCodeMappings;
    selectedJtCodes.clear();
    selectedJtCodes = selectedJtCodes;
    error = null;
    success = 'Cleared expense mappings for selected codes. Click Save Expense Mappings to persist.';
  }

  function handleClearSales() {
    if (selectedJtCodes.size === 0) {
      error = 'Please select at least one JobTread cost code to clear.';
      return;
    }
    selectedJtCodes.forEach(jtNumber => { delete salesCodeMappings[jtNumber]; });
    salesCodeMappings = salesCodeMappings;
    selectedJtCodes.clear();
    selectedJtCodes = selectedJtCodes;
    error = null;
    success = 'Cleared sales mappings for selected codes. Click Save Sales Mappings to persist.';
  }

  async function saveExpenseMappings() {
    if (savingExpense) return;
    savingExpense = true;
    error = null;
    success = null;
    try {
      const response = await fetch('/api/users/cost-code-mappings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings: costCodeMappings })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        success = '✅ Expense mappings saved successfully.';
        setTimeout(() => { success = null; }, 3000);
      } else {
        error = data.error || 'Failed to save expense mappings';
      }
    } catch (err) {
      console.error('Error saving expense mappings:', err);
      error = 'Failed to save expense mappings. ' + err.message;
    } finally {
      savingExpense = false;
    }
  }

  async function saveSalesMappings() {
    if (savingSales) return;
    savingSales = true;
    error = null;
    success = null;
    try {
      const response = await fetch('/api/users/sales-code-mappings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings: salesCodeMappings })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        success = '✅ Sales mappings saved successfully.';
        setTimeout(() => { success = null; }, 3000);
      } else {
        error = data.error || 'Failed to save sales mappings';
      }
    } catch (err) {
      console.error('Error saving sales mappings:', err);
      error = 'Failed to save sales mappings. ' + err.message;
    } finally {
      savingSales = false;
    }
  }

  function getXeroAccountDisplay(code) {
    const account = xeroAccounts.find(a => a.code === code);
    return account ? `${account.code} - ${account.name}` : code;
  }

  function goBack() {
    navigate('/settings/xero');
  }
</script>

<Layout activeRoute="settings">
  <div class="container mx-auto px-4 py-8">
    <div class="mb-6">
      <button
        on:click={goBack}
        class="text-blue-600 hover:text-blue-800 mb-4"
      >
        ← Back to Settings
      </button>
      <h1 class="text-2xl font-bold">JobTread → Xero Cost Code Mappings</h1>
      <p class="text-gray-600 mt-2">
        Map your JobTread parent cost codes to Xero chart of accounts.
        Each JT cost code maps to a Xero account on two sides:
        an <strong>Expense / Bills</strong> account (used when JobTread bills sync to Xero)
        and a <strong>Sales / Invoice</strong> account (used when JobTread customer invoices sync to Xero).
      </p>
    </div>

    <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
      <p class="text-sm text-blue-800 mb-2">📖 <strong>How to set up your mappings:</strong></p>
      <ol class="text-sm text-blue-800 list-decimal list-inside space-y-1 mb-3">
        <li><strong>Step 1:</strong> Tick the cost codes you want to map (parent rows expand with ▶ to show children).</li>
        <li><strong>Step 2:</strong> Pick a Xero account in the <strong>Expense / Bills</strong> selector at the bottom to set the bill-side mapping.</li>
        <li><strong>Step 3:</strong> Pick a Xero account in the <strong>Sales / Invoice</strong> selector to set the invoice-side mapping. These can be (and usually are) different accounts.</li>
        <li><strong>Step 4:</strong> Click <strong>Save Expense Mappings</strong> and/or <strong>Save Sales Mappings</strong> to persist.</li>
      </ol>
      <p class="text-xs text-blue-700 mt-2">
        💡 <strong>Tip:</strong> Children inherit their parent's mapping (on each side independently) unless you map them specifically. The Sales side typically uses revenue or cost-of-goods-sold accounts.
      </p>
    </div>

    {#if managedByOwner}
      <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p class="text-sm text-blue-800">
          These mappings are managed by your organisation owner and are shown read-only.
          Contact your organisation owner to change them.
        </p>
      </div>
    {/if}

    {#if loading}
      <div class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span class="ml-3 text-gray-600">Loading...</span>
      </div>
    {:else if error && !jtCostCodes.length}
      <div class="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        {error}
      </div>
      <button
        on:click={goBack}
        class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
      >
        Go to Settings
      </button>
    {:else}
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="p-6">

          <!-- Migration banner: prompts users with existing expense mappings
               to also set up sales mappings now that they're a separate concept. -->
          {#if missingSalesMappings > 0 && !salesMappingBannerDismissed}
            <div class="mb-4 p-4 bg-orange-50 border-l-4 border-orange-500 rounded relative pr-10">
              <button
                type="button"
                on:click={dismissSalesMappingBanner}
                class="absolute top-3 right-3 text-orange-700 hover:text-orange-900 p-1 rounded hover:bg-orange-100"
                aria-label="Dismiss"
                title="Dismiss"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <p class="text-sm font-semibold text-orange-900">
                Optional -- set up Sales Mappings for your cost codes
              </p>
              <p class="text-sm text-orange-800 mt-1">
                You have <strong>{missingSalesMappings}</strong> cost code(s) with an Expense account but no Sales account.
                Customer invoices will fall back to your default Xero invoice code unless you map each cost code's Sales side here too.
              </p>
              <p class="text-xs text-orange-700 mt-2">
                Tip: Sales mappings usually use Revenue or Cost-of-Goods-Sold (COGS) accounts — different from the Expense accounts you use for bills.
              </p>
            </div>
          {/if}

          {#if error}
            <div class="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          {/if}

          {#if success}
            <div class="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          {/if}

          <!-- Cost Code Heading -->
          <h2 class="text-xl font-semibold text-gray-800 mb-4">JobTread Parent CostCodes</h2>

          <!-- Cost Code List -->
          <div class="border rounded-md overflow-hidden">
            <div class="bg-gray-50 px-4 py-3 border-b font-semibold text-sm text-gray-700 grid grid-cols-12 gap-3">
              <div class="col-span-1">Select</div>
              <div class="col-span-2">Cost Code</div>
              <div class="col-span-3">Name</div>
              <div class="col-span-3">Expense / Bills</div>
              <div class="col-span-3">Sales / Invoice</div>
            </div>

            <div class="max-h-96 overflow-y-auto">
              {#each jtCostCodes as code}
                <!-- Parent Row -->
                <div class="px-4 py-3 border-b hover:bg-[#f0f4ff] grid grid-cols-12 gap-3 items-center">
                  <div class="col-span-1">
                    <input
                      type="checkbox"
                      checked={selectedJtCodes.has(code.number)}
                      on:change={() => toggleSelection(code.number)}
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div class="col-span-2 font-mono text-sm flex items-center">
                    {#if code.children && code.children.length > 0}
                      <button
                        on:click={() => toggleExpand(code.number)}
                        class="mr-2 text-blue-600 hover:text-blue-800 font-bold w-5 text-center"
                        title={expandedParents.has(code.number) ? 'Collapse' : 'Expand'}
                      >
                        {expandedParents.has(code.number) ? '▼' : '▶'}
                      </button>
                    {:else}
                      <span class="w-5 mr-2"></span>
                    {/if}
                    {code.number}
                  </div>
                  <div class="col-span-3 text-sm flex items-center">
                    <span class="font-medium">{code.name}</span>
                    {#if code.children && code.children.length > 0}
                      <span class="ml-2 text-xs text-gray-500">({code.children.length} children)</span>
                    {/if}
                  </div>
                  <div class="col-span-3 text-sm">
                    {#if costCodeMappings[code.number]}
                      <span class="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-semibold text-xs">
                        {getXeroAccountDisplay(costCodeMappings[code.number])}
                      </span>
                    {:else}
                      <span class="text-gray-400 italic text-xs">Not mapped</span>
                    {/if}
                  </div>
                  <div class="col-span-3 text-sm">
                    {#if salesCodeMappings[code.number]}
                      <span class="inline-block bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-semibold text-xs">
                        {getXeroAccountDisplay(salesCodeMappings[code.number])}
                      </span>
                    {:else}
                      <span class="text-gray-400 italic text-xs">Not mapped</span>
                    {/if}
                  </div>
                </div>

                <!-- Child Rows (shown when parent is expanded) -->
                {#if expandedParents.has(code.number) && code.children && code.children.length > 0}
                  {#each code.children as child}
                    <div class="px-4 py-3 border-b hover:bg-blue-50 bg-blue-50 grid grid-cols-12 gap-3 items-center">
                      <div class="col-span-1 pl-8">
                        <input
                          type="checkbox"
                          checked={selectedJtCodes.has(child.number)}
                          on:change={() => toggleSelection(child.number)}
                          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div class="col-span-2 font-mono text-sm pl-7 text-gray-700">
                        ↳ {child.number}
                      </div>
                      <div class="col-span-3 text-sm text-gray-700">
                        {child.name}
                      </div>
                      <!-- Expense (Bills) cell — show child-specific, else inherit parent's, else 'Not mapped' -->
                      <div class="col-span-3 text-sm">
                        {#if costCodeMappings[child.number]}
                          <span class="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-semibold text-xs">
                            {getXeroAccountDisplay(costCodeMappings[child.number])}
                          </span>
                          <span class="text-xs text-gray-500 ml-1">(specific)</span>
                        {:else if costCodeMappings[code.number]}
                          <span class="inline-block bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full italic text-xs">
                            {getXeroAccountDisplay(costCodeMappings[code.number])}
                          </span>
                          <span class="text-xs text-gray-500 ml-1">(inherited)</span>
                        {:else}
                          <span class="text-gray-400 italic text-xs">Not mapped</span>
                        {/if}
                      </div>
                      <!-- Sales (Invoice) cell — same inheritance rule applies independently -->
                      <div class="col-span-3 text-sm">
                        {#if salesCodeMappings[child.number]}
                          <span class="inline-block bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-semibold text-xs">
                            {getXeroAccountDisplay(salesCodeMappings[child.number])}
                          </span>
                          <span class="text-xs text-gray-500 ml-1">(specific)</span>
                        {:else if salesCodeMappings[code.number]}
                          <span class="inline-block bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full italic text-xs">
                            {getXeroAccountDisplay(salesCodeMappings[code.number])}
                          </span>
                          <span class="text-xs text-gray-500 ml-1">(inherited)</span>
                        {:else}
                          <span class="text-gray-400 italic text-xs">Not mapped</span>
                        {/if}
                      </div>
                    </div>
                  {/each}
                {/if}
              {/each}
            </div>
          </div>

          <!-- Refresh from Xero -->
          <div class="mt-4 flex items-center justify-between gap-3">
            <p class="text-sm text-gray-600">
              {selectedJtCodes.size} cost code(s) selected · {xeroAccounts.length} Xero accounts loaded
            </p>
            <button
              on:click={() => refreshXeroAccounts(true)}
              disabled={refreshing}
              class="text-sm bg-indigo-600 text-white px-3 py-1 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              title="Re-fetch all accounts from Xero to pick up any new or changed accounts"
            >
              {#if refreshing}
                <div class="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                Refreshing...
              {:else}
                ↻ Refresh from Xero
              {/if}
            </button>
          </div>

          <!-- Side-by-side mapping assignment selectors -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">

            <!-- Expense / Bills selector -->
            <div class="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <label for="xeroExpenseSelect" class="block text-sm font-semibold text-blue-900 mb-1">
                Expense / Bills account
              </label>
              <p class="text-xs text-blue-700 mb-2">
                Used when JobTread bills sync to Xero. Pick an expense, purchase, or COGS account.
              </p>
              <div class="flex gap-2">
                <select
                  id="xeroExpenseSelect"
                  bind:value={selectedExpenseCode}
                  on:change={handleExpenseAssignment}
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- Assign expense account --</option>
                  {#each xeroAccounts as account}
                    <option value={account.code}>{account.code} - {account.name}</option>
                  {/each}
                </select>
                <button
                  on:click={handleClearExpense}
                  disabled={selectedJtCodes.size === 0}
                  class="bg-red-600 text-white px-3 py-2 rounded-md text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  title="Remove the expense mapping from the selected JT codes"
                >
                  Clear
                </button>
              </div>
              <div class="mt-3 flex justify-end">
                <button
                  on:click={saveExpenseMappings}
                  disabled={savingExpense || managedByOwner}
                  class="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {#if savingExpense}
                    Saving…
                  {:else}
                    Save Expense Mappings
                  {/if}
                </button>
              </div>
            </div>

            <!-- Sales / Invoice selector -->
            <div class="p-4 bg-purple-50 border border-purple-200 rounded-md">
              <label for="xeroSalesSelect" class="block text-sm font-semibold text-purple-900 mb-1">
                Sales / Invoice account
              </label>
              <p class="text-xs text-purple-700 mb-2">
                Used when JobTread customer invoices sync to Xero. Pick a revenue or COGS account.
              </p>
              <div class="flex gap-2">
                <select
                  id="xeroSalesSelect"
                  bind:value={selectedSalesCode}
                  on:change={handleSalesAssignment}
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- Assign sales account --</option>
                  {#each xeroAccounts as account}
                    <option value={account.code}>{account.code} - {account.name}</option>
                  {/each}
                </select>
                <button
                  on:click={handleClearSales}
                  disabled={selectedJtCodes.size === 0}
                  class="bg-red-600 text-white px-3 py-2 rounded-md text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  title="Remove the sales mapping from the selected JT codes"
                >
                  Clear
                </button>
              </div>
              <div class="mt-3 flex justify-end">
                <button
                  on:click={saveSalesMappings}
                  disabled={savingSales || managedByOwner}
                  class="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {#if savingSales}
                    Saving…
                  {:else}
                    Save Sales Mappings
                  {/if}
                </button>
              </div>
            </div>

          </div>

          <!-- Bottom navigation -->
          <div class="mt-6 flex justify-end gap-3">
            <button
              on:click={goBack}
              class="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600"
            >
              Back to Settings
            </button>
          </div>
        </div>
      </div>
    {/if}
  </div>
</Layout>

<style>
  .container {
    max-width: 1400px;
  }
</style>
